import type { Landform, MapDefinition, Point, TerrainPlateau, TerrainRamp, TerrainRegion, TerrainRoof } from './world-types.js';

const smooth = (t: number) => { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); };
const shorelines = new WeakMap<TerrainRegion[], TerrainRegion[]>();
function shorelineAt(map: MapDefinition, z: number) {
  const source = map.scenery?.shoreline;
  if (!source?.length) return undefined;
  let shelves = shorelines.get(source);
  if (!shelves) { shelves = [...source].sort((a, b) => a.z - b.z); shorelines.set(source, shelves); }
  let low = 0, high = shelves.length - 1;
  while (low <= high) {
    const mid = (low + high) >>> 1, shelf = shelves[mid];
    if (z < shelf.z - shelf.depth / 2) high = mid - 1;
    else if (z > shelf.z + shelf.depth / 2) low = mid + 1;
    else return shelf;
  }
  return undefined;
}

export function landformWeight(form: Landform, x: number, z: number): number {
  const u = (x - form.x) / (form.width * .5), v = (z - form.z) / (form.depth * .5);
  if (Math.abs(u) >= 1 || Math.abs(v) >= 1) return 0;
  const angle = Math.atan2(v, u), edge = .94 + Math.sin(angle * 3 + 1) * .025 + Math.sin(angle * 5 - 2) * .02;
  return Math.max(0, 1 - (u * u + v * v) / (edge * edge));
}

/** The fine lattice of the ground mesh: every vertex of the mesh is one of its points, and every cell splits along the same diagonal. */
export function terrainGrid(map: MapDefinition) {
  return terrainMesh(map);
}

/** Coarse cells per tile side: a tile is drawn either fine or coarse as a whole. */
const TILE_COARSE_CELLS = 8;

/**
 * The ground mesh's layout. With `coarseCellSize`, tiles of gentle ground use the coarse grid and only tiles that plateau
 * edges or ramps can make sharp use the fine one. A fine tile's border with a coarse tile takes the coarse edge's straight
 * line, so the two meet without cracks; every height on the mesh, here and in the browser, follows that one rule.
 */
export interface TerrainMesh {
  columns: number; rows: number; dx: number; dz: number;
  /** Fine cells per coarse cell; 1 draws the whole map fine. */
  ratio: number;
  /** Fine cells per tile side, and the tile flags, 1 where the tile is fine. */
  tile: number; tileColumns: number; tileRows: number; fine: Uint8Array;
  /** Lattice heights worked out so far (NaN where not yet): cliffs, the drawn ground and the sampler share them. */
  heights?: Float32Array;
  /** Per cave level, the lattice heights of its floor within the reach of its roofs (beyond, it shares `heights`). */
  floors?: Map<number, Floors | null>;
}
type Floors = { i0: number; j0: number; i1: number; j1: number; heights: Float32Array };
const meshes = new WeakMap<object, { width: number; depth: number; mesh: TerrainMesh }>();
export function terrainMesh(map: MapDefinition): TerrainMesh {
  const terrain = map.terrain, key = terrain ?? map, cached = meshes.get(key);
  if (cached && cached.width === map.width && cached.depth === map.depth) return cached.mesh;
  const cell = terrain?.cellSize ?? .5, ratio = Math.max(1, Math.round((terrain?.coarseCellSize ?? cell) / cell));
  const columns = Math.max(1, ratio * Math.ceil(map.width / (cell * ratio))), rows = Math.max(1, ratio * Math.ceil(map.depth / (cell * ratio)));
  const tile = ratio * TILE_COARSE_CELLS, tileColumns = Math.ceil(columns / tile), tileRows = Math.ceil(rows / tile);
  const fine = new Uint8Array(tileColumns * tileRows).fill(ratio === 1 ? 1 : 0);
  const mesh: TerrainMesh = { columns, rows, dx: map.width / columns, dz: map.depth / rows, ratio, tile, tileColumns, tileRows, fine };
  if (ratio > 1 && terrain) {
    // Sharp ground comes only from plateau edges and ramps; landforms, levels and the shore are smooth at the coarse grid.
    const soft = cell * 2, size = { x: tile * mesh.dx, z: tile * mesh.dz };
    // Marks the tiles within `reach` of a segment, testing only those its bounds can touch.
    const mark = (a: Point, b: Point, reach: number) => {
      const column0 = Math.max(0, Math.floor((Math.min(a.x, b.x) - reach + map.width / 2) / size.x)), column1 = Math.min(tileColumns - 1, Math.floor((Math.max(a.x, b.x) + reach + map.width / 2) / size.x));
      const row0 = Math.max(0, Math.floor((Math.min(a.z, b.z) - reach + map.depth / 2) / size.z)), row1 = Math.min(tileRows - 1, Math.floor((Math.max(a.z, b.z) + reach + map.depth / 2) / size.z));
      for (let row = row0; row <= row1; row++) for (let column = column0; column <= column1; column++) {
        const minX = column * size.x - map.width / 2, minZ = row * size.z - map.depth / 2;
        if (!fine[row * tileColumns + column] && segmentBoxDistance(a, b, minX, minX + size.x, minZ, minZ + size.z) <= reach) fine[row * tileColumns + column] = 1;
      }
    };
    for (const plateau of terrain.plateaus ?? []) {
      const edges = typeof plateau.edge === 'number' ? plateau.outline.map(() => plateau.edge as number) : plateau.outline.map((_, i) => (plateau.edge as number[] | undefined)?.[i] ?? 0);
      // A long run is a gentle slope away from its rim, which the coarse grid draws well.
      const reach = soft + Math.min(4, Math.max(...edges)) + cell;
      plateau.outline.forEach((a, i) => mark(a, plateau.outline[(i + 1) % plateau.outline.length], reach));
    }
    for (const ramp of terrain.ramps ?? []) mark(ramp.from, ramp.to, ramp.width + (ramp.bank ?? 1) + cell);
    for (const roof of terrain.roofs ?? []) roof.outline.forEach((a, i) => mark(a, roof.outline[(i + 1) % roof.outline.length], soft + cell));
  }
  meshes.set(key, { width: map.width, depth: map.depth, mesh });
  return mesh;
}

/** The lattice heights worked out for `map` so far, kept with a stored map so the next process need not work them out again. */
export function terrainHeightCache(map: MapDefinition): Pick<TerrainMesh, 'heights' | 'floors'> {
  const { heights, floors } = terrainMesh(map);
  return { heights, floors };
}
/** Hands a map read from storage the lattice heights `terrainHeightCache` kept with it. */
export function seedTerrainHeights(map: MapDefinition, cache: Pick<TerrainMesh, 'heights' | 'floors'>) {
  const mesh = terrainMesh(map);
  // A cache from a map of another size is ignored: the heights are worked out again.
  if (cache.heights?.length === (mesh.columns + 1) * (mesh.rows + 1)) mesh.heights = cache.heights;
  if (cache.floors) mesh.floors = cache.floors;
}

/** Distance from a segment to an axis-aligned box (0 when they touch), sampled finely enough for tile tests. */
function segmentBoxDistance(a: Point, b: Point, minX: number, maxX: number, minZ: number, maxZ: number) {
  let best = Infinity;
  for (let i = 0, steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 2)); i <= steps; i++) {
    const x = a.x + (b.x - a.x) * i / steps, z = a.z + (b.z - a.z) * i / steps;
    best = Math.min(best, Math.hypot(Math.max(minX - x, 0, x - maxX), Math.max(minZ - z, 0, z - maxZ)));
  }
  return best;
}

const fineTileAt = (mesh: TerrainMesh, column: number, row: number) =>
  column >= 0 && row >= 0 && column < mesh.columns && row < mesh.rows && mesh.fine[Math.floor(row / mesh.tile) * mesh.tileColumns + Math.floor(column / mesh.tile)] === 1;

/**
 * Height of the mesh vertex at lattice point (i, j). A point between coarse corners that lies on a fine tile's border with a
 * coarse tile follows the coarse edge's straight line; every other vertex takes the terrain's own height.
 */
export function terrainLatticeHeight(map: MapDefinition, i: number, j: number): number {
  const mesh = terrainMesh(map);
  if (map.level) {
    // A cave level keeps its own floor heights only within reach of its roofs; everywhere else its ground is the surface's.
    const floors = floorsOf(map, mesh, map.level);
    if (floors && i >= floors.i0 && i <= floors.i1 && j >= floors.j0 && j <= floors.j1) {
      const at = (j - floors.j0) * (floors.i1 - floors.i0 + 1) + i - floors.i0, known = floors.heights[at];
      if (!Number.isNaN(known)) return known;
      // Clear of its own roofs, the floor is the surface's ground, already worked out (or shared with it once it is).
      const x = i * mesh.dx - map.width / 2, z = j * mesh.dz - map.depth / 2;
      return floors.heights[at] = roofWeight(map, x, z, map.level) > 0 ? latticeHeight(map, mesh, i, j) : terrainLatticeHeight({ ...map, level: undefined }, i, j);
    }
  }
  const index = j * (mesh.columns + 1) + i;
  const heights = mesh.heights ??= new Float32Array((mesh.columns + 1) * (mesh.rows + 1)).fill(NaN), known = heights[index];
  return Number.isNaN(known) ? (heights[index] = latticeHeight(map, mesh, i, j)) : known;
}

/** The lattice window a cave level's roofs reach, a few metres wide of them so no height beyond it depends on them. */
function floorsOf(map: MapDefinition, mesh: TerrainMesh, level: number): Floors | null {
  const known = mesh.floors?.get(level);
  if (known !== undefined) return known;
  const points = (map.terrain?.roofs ?? []).filter(roof => roof.level === level).flatMap(roof => roof.outline);
  let floors: Floors | null = null;
  if (points.length) {
    const margin = 3 + (map.terrain?.cellSize ?? .5) * 2 * mesh.ratio;
    const i0 = Math.max(0, Math.floor((Math.min(...points.map(p => p.x)) - margin + map.width / 2) / mesh.dx)), i1 = Math.min(mesh.columns, Math.ceil((Math.max(...points.map(p => p.x)) + margin + map.width / 2) / mesh.dx));
    const j0 = Math.max(0, Math.floor((Math.min(...points.map(p => p.z)) - margin + map.depth / 2) / mesh.dz)), j1 = Math.min(mesh.rows, Math.ceil((Math.max(...points.map(p => p.z)) + margin + map.depth / 2) / mesh.dz));
    // Roofs wholly outside the map (a map cropped below its roofs) leave an empty window.
    if (i0 <= i1 && j0 <= j1) floors = { i0, j0, i1, j1, heights: new Float32Array((i1 - i0 + 1) * (j1 - j0 + 1)).fill(NaN) };
  }
  (mesh.floors ??= new Map()).set(level, floors);
  return floors;
}

/** The lattice range `[i0, i1] × [j0, j1]` within which a cave level's floor differs from the surface, or null for none. */
export function caveWindow(map: MapDefinition, level: number) {
  const floors = map.terrain ? floorsOf(map, terrainMesh(map), level) : null;
  return floors && { i0: floors.i0, j0: floors.j0, i1: floors.i1, j1: floors.j1 };
}

function latticeHeight(map: MapDefinition, mesh: TerrainMesh, i: number, j: number): number {
  const k = mesh.ratio, x = i * mesh.dx - map.width / 2, z = j * mesh.dz - map.depth / 2;
  if (k === 1 || (i % k === 0 && j % k === 0)) return terrainVertexHeight(map, x, z);
  if (i % mesh.tile === 0 && !(fineTileAt(mesh, i - 1, j) && fineTileAt(mesh, i, j))) {
    const j0 = j - j % k, t = (j - j0) / k;
    return Math.fround(terrainVertexHeight(map, x, j0 * mesh.dz - map.depth / 2) * (1 - t) + terrainVertexHeight(map, x, (j0 + k) * mesh.dz - map.depth / 2) * t);
  }
  if (j % mesh.tile === 0 && !(fineTileAt(mesh, i, j - 1) && fineTileAt(mesh, i, j))) {
    const i0 = i - i % k, t = (i - i0) / k;
    return Math.fround(terrainVertexHeight(map, i0 * mesh.dx - map.width / 2, z) * (1 - t) + terrainVertexHeight(map, (i0 + k) * mesh.dx - map.width / 2, z) * t);
  }
  return terrainVertexHeight(map, x, z);
}

/** The mesh cell under a lattice cell: its corner (i, j) and its size in lattice steps (1 in a fine tile, `ratio` in a coarse one). */
export function terrainCellAt(map: MapDefinition, column: number, row: number) {
  const mesh = terrainMesh(map);
  if (mesh.ratio === 1 || fineTileAt(mesh, column, row)) return { i: column, j: row, size: 1 };
  return { i: column - column % mesh.ratio, j: row - row % mesh.ratio, size: mesh.ratio };
}

/** Height of a grid vertex; only the triangle sampler below is used to place actors. */
export function terrainVertexHeight(map: MapDefinition, x: number, z: number): number {
  if (!map.terrain) return 0;
  let height = raisedHeight(map, x, z);
  const ramps = map.terrain.ramps ?? [], bounds = rampBounds(ramps);
  for (let i = 0; i < ramps.length; i++) {
    const box = bounds[i];
    if (x >= box[0] && x <= box[1] && z >= box[2] && z <= box[3]) height = rampHeight(map, ramps[i], x, z, height);
  }
  if (map.terrain.roofs?.length) height = roofedHeight(map, x, z, height);
  return Math.fround(height);
}

// Roofs are found like plateaus: each as a sheer plateau of its outline, filed in the same buckets.
const roofShapes = new WeakMap<TerrainRoof[], TerrainPlateau[]>();
function nearbyRoofs(map: MapDefinition, x: number, z: number) {
  const roofs = map.terrain!.roofs!, cell = map.terrain!.cellSize ?? .5;
  let shapes = roofShapes.get(roofs);
  if (!shapes) roofShapes.set(roofs, shapes = roofs.map(roof => ({ outline: roof.outline, height: roof.height, edge: 0 })));
  const index = plateauIndex(shapes, cell);
  const bx = Math.floor((x - index.originX) / PLATEAU_BUCKET), bz = Math.floor((z - index.originZ) / PLATEAU_BUCKET);
  return { roofs, shapes, index, cell, nearby: bx >= 0 && bz >= 0 && bx < index.columns && bz < index.rows ? index.buckets[bz * index.columns + bx] ?? [] : [] };
}
/** How fully one level's roofs cover a point. */
function roofWeight(map: MapDefinition, x: number, z: number, level: number) {
  if (!map.terrain?.roofs?.length) return 0;
  const { roofs, shapes, index, cell, nearby } = nearbyRoofs(map, x, z);
  let weight = 0;
  for (const i of nearby) if (roofs[i].level === level) weight = Math.max(weight, plateauWeight(shapes[i], x, z, cell, index.shapes[i]));
  return weight;
}
/** The ground with every roof but the viewing level's own laid over it: filled up to each roof's top, never lowered. */
function roofedHeight(map: MapDefinition, x: number, z: number, height: number) {
  const { roofs, shapes, index, cell, nearby } = nearbyRoofs(map, x, z);
  let natural: number | undefined;
  for (const i of nearby) {
    if (roofs[i].level === map.level) continue;
    const weight = plateauWeight(shapes[i], x, z, cell, index.shapes[i]);
    if (weight <= 0) continue;
    const top = (natural ??= naturalHeight(map, x, z, 1)) + roofs[i].height;
    if (top > height) height += (top - height) * weight;
  }
  return height;
}

type Profile = 'scarp' | 'rounded' | 'scree';
const PROFILES: Record<Profile, (u: number) => number> = {
  scarp: u => 1 - smooth(u),
  // A rounded shoulder that steepens towards the foot.
  rounded: u => 1 - u * u,
  // Steepest at the rim, spreading out into a fan at the foot.
  scree: u => (1 - u) * (1 - u),
};

/**
 * 1 inside, 0 beyond the edge, and the plateau's profile across its edge. The fall starts a grid cell inside the outline
 * and is never narrower than two cells, so even a sheer edge follows the outline instead of stepping cell by cell.
 */
function plateauWeight(plateau: TerrainPlateau, x: number, z: number, cell: number, shape = plateauShape(plateau, cell * 2)) {
  const outline = plateau.outline, soft = cell * 2;
  if (x < shape.originX || z < shape.originZ || x > shape.maxX || z > shape.maxZ) return 0;
  const bx = Math.floor((x - shape.originX) / BUCKET), bz = Math.floor((z - shape.originZ) / BUCKET), key = bz * shape.columns + bx;
  // Inside or out: count crossings to the right, among only the sides that span this band of rows. A bucket no side comes
  // near is all inside or all outside, so its first answer stands for the rest of it.
  let inside = false;
  if (!shape.close[key] && shape.insides[key] >= 0) inside = shape.insides[key] === 1;
  else {
    for (const i of shape.bands[bz] ?? []) {
      const a = outline[i], b = outline[(i + outline.length - 1) % outline.length];
      if ((a.z > z) !== (b.z > z) && x < (b.x - a.x) * (z - a.z) / (b.z - a.z) + a.x) inside = !inside;
    }
    if (!shape.close[key]) shape.insides[key] = inside ? 1 : 0;
  }
  // Inside, the nearest side sets the start of the fall. Outside, each nearby side casts its own fall and the highest wins, so
  // where sides with different runs meet their falls join without a seam. A side casts its fall outward: fully ahead of it,
  // fading round its ends, and none back across the plateau.
  // Outside, sides come strongest-possible first, so the loop stops once no remaining side could raise the weight.
  const sides = inside ? shape.close[key] ?? [] : nearOrder(shape, outline, key, bx, bz, soft);
  let nearest = Infinity, weight = 0;
  for (let n = 0; n < sides.length; n++) {
    if (!inside && shape.bounds[key]![n] <= weight) break;
    const i = sides[n], j = (i + outline.length - 1) % outline.length, a = outline[i], b = outline[j];
    const dx = b.x - a.x, dz = b.z - a.z, t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz || 1)));
    const ox = x - a.x - dx * t, oz = z - a.z - dz * t, squared = ox * ox + oz * oz, reach = shape.reaches[i] - soft / 2;
    if (!inside && squared >= reach * reach) continue;
    const distance = Math.sqrt(squared), edge = shape.edges[i] + (shape.edges[j] - shape.edges[i]) * t;
    if (inside) { if (distance < nearest) { nearest = distance; weight = fall(-distance, edge, soft, shape.profiles[i], shape.profiles[j], t); } }
    else if (distance < edge + soft / 2) {
      const facing = distance < 1e-6 ? 1 : (dx * oz - dz * ox) * shape.turn / (shape.lengths[i] * distance);
      if (facing > -.6) weight = Math.max(weight, fall(distance, edge, soft, shape.profiles[i], shape.profiles[j], t) * smooth((facing + .6) / .6));
    }
  }
  return inside && nearest === Infinity ? 1 : weight;
}

/**
 * A bucket's nearby sides ordered by the most each could cast anywhere in the bucket: its fall at the bucket's nearest
 * approach to the side's bounds, under the fuller of its two profiles and the longer of its two runs, so no side is ever cut short.
 */
function nearOrder(shape: PlateauShape, outline: Point[], key: number, bx: number, bz: number, soft: number) {
  const known = shape.ordered[key];
  if (known) return known;
  const minX = shape.originX + bx * BUCKET, minZ = shape.originZ + bz * BUCKET;
  const sides = shape.near[key] ?? [], bounds = sides.map(i => {
    const a = outline[i], b = outline[(i + outline.length - 1) % outline.length];
    const gap = Math.hypot(Math.max(0, minX - Math.max(a.x, b.x), Math.min(a.x, b.x) - minX - BUCKET), Math.max(0, minZ - Math.max(a.z, b.z), Math.min(a.z, b.z) - minZ - BUCKET));
    const j = (i + outline.length - 1) % outline.length, u = (gap + soft / 2) / (Math.max(shape.edges[i], shape.edges[j]) + soft);
    return (u <= 0 ? 1 : u >= 1 ? 0 : Math.max(PROFILES[shape.profiles[i]](u), PROFILES[shape.profiles[j]](u))) + 1e-9;
  });
  const order = sides.map((_, n) => n).sort((p, q) => bounds[q] - bounds[p]);
  shape.bounds[key] = Float64Array.from(order, n => bounds[n]);
  return shape.ordered[key] = order.map(n => sides[n]);
}

/** The fall at a signed distance from a side (negative inside); between points with different profiles it blends from one to the other. */
function fall(signed: number, edge: number, soft: number, from: Profile, to: Profile, mix: number) {
  const u = (signed + soft / 2) / (edge + soft);
  if (u <= 0) return 1;
  if (u >= 1) return 0;
  return from === to ? PROFILES[from](u) : PROFILES[from](u) * (1 - mix) + PROFILES[to](u) * mix;
}

// Long rugged outlines have hundreds of sides; each point looks only at the sides near it, found through square buckets.
const BUCKET = 8;
type PlateauShape = { edges: number[]; profiles: Profile[]; reaches: number[]; lengths: number[]; turn: number; originX: number; originZ: number; maxX: number; maxZ: number; columns: number; near: (number[] | undefined)[]; close: (number[] | undefined)[]; insides: Int8Array; bands: number[][];
  /** Per bucket, filled on first use: its near sides strongest-possible first, and the most each could cast there. */
  ordered: (number[] | undefined)[]; bounds: (Float64Array | undefined)[] };
const plateauShapes = new WeakMap<TerrainPlateau, PlateauShape>();
function plateauShape(plateau: TerrainPlateau, soft: number): PlateauShape {
  const cached = plateauShapes.get(plateau);
  if (cached) return cached;
  const outline = plateau.outline;
  const edges = typeof plateau.edge === 'number' ? outline.map(() => plateau.edge as number) : outline.map((_, i) => (plateau.edge as number[] | undefined)?.[i] ?? 0);
  const profiles = outline.map((_, i) => (Array.isArray(plateau.profile) ? plateau.profile[i] : plateau.profile) ?? 'scarp');
  // A side affects ground within its fall: the larger of its two ends' runs, plus the softening either side of the outline.
  const reaches = outline.map((_, i) => Math.max(edges[i], edges[(i + outline.length - 1) % outline.length]) + soft);
  const reach = Math.max(...reaches);
  const originX = Math.min(...outline.map(p => p.x)) - reach, originZ = Math.min(...outline.map(p => p.z)) - reach;
  const maxX = Math.max(...outline.map(p => p.x)) + reach, maxZ = Math.max(...outline.map(p => p.z)) + reach;
  const columns = Math.ceil((maxX - originX) / BUCKET) + 1, rows = Math.ceil((maxZ - originZ) / BUCKET) + 1, bands: number[][] = [];
  const near: (number[] | undefined)[] = new Array(columns * rows), close: (number[] | undefined)[] = new Array(columns * rows);
  const file = (buckets: (number[] | undefined)[], a: Point, b: Point, r: number, i: number) => {
    for (let bz = Math.floor((Math.min(a.z, b.z) - r - originZ) / BUCKET); bz <= Math.floor((Math.max(a.z, b.z) + r - originZ) / BUCKET); bz++)
      for (let bx = Math.floor((Math.min(a.x, b.x) - r - originX) / BUCKET); bx <= Math.floor((Math.max(a.x, b.x) + r - originX) / BUCKET); bx++)
        (buckets[bz * columns + bx] ??= []).push(i);
  };
  outline.forEach((a, i) => {
    const b = outline[(i + outline.length - 1) % outline.length];
    // Outside, a side reaches as far as its fall; inside, only the softening just within the outline is not yet full height.
    file(near, a, b, reaches[i], i); file(close, a, b, soft, i);
    for (let bz = Math.floor((Math.min(a.z, b.z) - originZ) / BUCKET); bz <= Math.floor((Math.max(a.z, b.z) - originZ) / BUCKET); bz++) (bands[bz] ??= []).push(i);
  });
  // Which way the outline winds: its inside lies to the left of each side going round when positive.
  const turn = Math.sign(outline.reduce((sum, a, i) => { const b = outline[(i + outline.length - 1) % outline.length]; return sum + b.x * a.z - a.x * b.z; }, 0));
  const lengths = outline.map((a, i) => { const b = outline[(i + outline.length - 1) % outline.length]; return Math.sqrt((b.x - a.x) ** 2 + (b.z - a.z) ** 2); });
  const shape = { edges, profiles, reaches, lengths, turn, originX, originZ, maxX, maxZ, columns, near, close, insides: new Int8Array(columns * rows).fill(-1), bands,
    ordered: new Array(columns * rows), bounds: new Array(columns * rows) };
  plateauShapes.set(plateau, shape);
  return shape;
}

/** Natural ground plus plateaus: everything ramps are laid against. */
function raisedHeight(map: MapDefinition, x: number, z: number) {
  let lift = 0, cover = 0;
  const plateaus = map.terrain!.plateaus;
  if (plateaus?.length) {
    const cell = map.terrain!.cellSize ?? .5, index = plateauIndex(plateaus, cell);
    const bx = Math.floor((x - index.originX) / PLATEAU_BUCKET), bz = Math.floor((z - index.originZ) / PLATEAU_BUCKET);
    const nearby = bx >= 0 && bz >= 0 && bx < index.columns && bz < index.rows ? index.buckets[bz * index.columns + bx] : undefined;
    for (const i of nearby ?? []) {
      const plateau = plateaus[i], weight = plateauWeight(plateau, x, z, cell, index.shapes[i]);
      if (weight > 0) { lift += plateau.height * weight; if (plateau.height > 0) cover = Math.max(cover, weight); }
    }
  }
  return naturalHeight(map, x, z, cover) + lift;
}

// A large map has dozens of plateaus, each covering a small part of it; a point looks only at those whose reach covers its bucket.
const PLATEAU_BUCKET = 16;
type PlateauIndex = { count: number; shapes: PlateauShape[]; originX: number; originZ: number; columns: number; rows: number; buckets: (number[] | undefined)[] };
const plateauIndexes = new WeakMap<TerrainPlateau[], PlateauIndex>();
function plateauIndex(plateaus: TerrainPlateau[], cell: number): PlateauIndex {
  // Rebuilt when a map builder adds plateaus after asking for a height.
  const cached = plateauIndexes.get(plateaus);
  if (cached?.count === plateaus.length) return cached;
  const shapes = plateaus.map(plateau => plateauShape(plateau, cell * 2));
  const originX = Math.min(...shapes.map(shape => shape.originX)), originZ = Math.min(...shapes.map(shape => shape.originZ));
  const columns = Math.floor((Math.max(...shapes.map(shape => shape.maxX)) - originX) / PLATEAU_BUCKET) + 1;
  const rows = Math.floor((Math.max(...shapes.map(shape => shape.maxZ)) - originZ) / PLATEAU_BUCKET) + 1;
  const buckets: (number[] | undefined)[] = new Array(columns * rows);
  // Filed in plateau order, so their lifts add up in the same order as over the whole list.
  shapes.forEach((shape, i) => {
    for (let bz = Math.floor((shape.originZ - originZ) / PLATEAU_BUCKET); bz <= Math.floor((shape.maxZ - originZ) / PLATEAU_BUCKET); bz++)
      for (let bx = Math.floor((shape.originX - originX) / PLATEAU_BUCKET); bx <= Math.floor((shape.maxX - originX) / PLATEAU_BUCKET); bx++)
        (buckets[bz * columns + bx] ??= []).push(i);
  });
  const index = { count: plateaus.length, shapes, originX, originZ, columns, rows, buckets };
  plateauIndexes.set(plateaus, index);
  return index;
}

const rampEnds = new WeakMap<TerrainRamp, { map: MapDefinition; low: number; high: number }>();
/** Each ramp's reach as [minX, maxX, minZ, maxZ]: its length, the level run past either end and its banks. */
const rampBoxes = new WeakMap<TerrainRamp[], number[][]>();
function rampBounds(ramps: TerrainRamp[]) {
  let boxes = rampBoxes.get(ramps);
  if (!boxes) rampBoxes.set(ramps, boxes = ramps.map(ramp => {
    const reach = ramp.width + (ramp.bank ?? 1);
    return [Math.min(ramp.from.x, ramp.to.x) - reach, Math.max(ramp.from.x, ramp.to.x) + reach, Math.min(ramp.from.z, ramp.to.z) - reach, Math.max(ramp.from.z, ramp.to.z) + reach];
  }));
  return boxes;
}
function rampHeight(map: MapDefinition, ramp: TerrainRamp, x: number, z: number, height: number) {
  const dx = ramp.to.x - ramp.from.x, dz = ramp.to.z - ramp.from.z, length = Math.hypot(dx, dz);
  if (!length) return height;
  // Each end takes the height of the ground half the ramp's width beyond it and runs on level that far, so the ramp meets a
  // slanted edge flush at either end.
  const t = ((x - ramp.from.x) * dx + (z - ramp.from.z) * dz) / (length * length);
  if (-t * length > ramp.width / 2 || (t - 1) * length > ramp.width / 2) return height;
  const across = Math.abs((x - ramp.from.x) * dz - (z - ramp.from.z) * dx) / length - ramp.width / 2;
  const bank = ramp.bank ?? 1;
  if (across >= bank) return height;
  let ends = rampEnds.get(ramp);
  if (!ends || ends.map !== map) rampEnds.set(ramp, ends = { map,
    low: ramp.heights?.[0] ?? raisedHeight(map, ramp.from.x - dx / length * ramp.width / 2, ramp.from.z - dz / length * ramp.width / 2),
    high: ramp.heights?.[1] ?? raisedHeight(map, ramp.to.x + dx / length * ramp.width / 2, ramp.to.z + dz / length * ramp.width / 2) });
  const surface = ends.low + (ends.high - ends.low) * Math.max(0, Math.min(1, t)), weight = across <= 0 ? 1 : 1 - smooth(across / bank);
  return height + Math.max(0, surface - height) * weight;
}

const lattice = (i: number, j: number, seed: number) => { const v = Math.sin(i * 127.1 + j * 311.7 + seed * 74.7) * 43758.5453; return v - Math.floor(v); };
function valueNoise(x: number, z: number, seed: number) {
  const i = Math.floor(x), j = Math.floor(z), u = smooth(x - i), v = smooth(z - j);
  const a = lattice(i, j, seed), b = lattice(i + 1, j, seed), c = lattice(i, j + 1, seed), d = lattice(i + 1, j + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
/** Rolling ground between 0 and 1: four octaves of smooth noise, the broad folds outweighing the small ones. */
function rolling(x: number, z: number, seed: number) {
  let sum = 0, weight = .5, total = 0;
  for (let octave = 0; octave < 4; octave++) { sum += valueNoise(x, z, seed + octave * 17) * weight; total += weight; x = x * 2.03 + 5.2; z = z * 2.03 - 3.1; weight *= .45; }
  return sum / total;
}

// Landforms, relief and levels each shape only their own part of a large map; a point looks only at those that reach its bucket.
const FEATURE_BUCKET = 16;
type GroundFeatures = { landforms: Landform[]; relief: NonNullable<MapDefinition['terrain']>['relief'] & {}; levels: NonNullable<MapDefinition['terrain']>['levels'] & {} };
type FeatureIndex = { width: number; depth: number; landforms: number; relief: number; levels: number; columns: number; rows: number; all: GroundFeatures; buckets: GroundFeatures[] };
const featureIndexes = new WeakMap<object, FeatureIndex>();
function groundFeaturesAt(map: MapDefinition, x: number, z: number): GroundFeatures {
  const terrain = map.terrain!;
  let index = featureIndexes.get(terrain);
  // Rebuilt when the map is resized or a map builder adds features after asking for a height.
  if (!index || index.width !== map.width || index.depth !== map.depth || index.landforms !== terrain.landforms.length || index.relief !== (terrain.relief?.length ?? 0) || index.levels !== (terrain.levels?.length ?? 0)) featureIndexes.set(terrain, index = featureIndex(map));
  const column = Math.floor((x + map.width / 2) / FEATURE_BUCKET), row = Math.floor((z + map.depth / 2) / FEATURE_BUCKET);
  return column >= 0 && row >= 0 && column < index.columns && row < index.rows ? index.buckets[row * index.columns + column] : index.all;
}
function featureIndex(map: MapDefinition): FeatureIndex {
  const terrain = map.terrain!, columns = Math.ceil(map.width / FEATURE_BUCKET) + 1, rows = Math.ceil(map.depth / FEATURE_BUCKET) + 1;
  const all: GroundFeatures = { landforms: terrain.landforms, relief: terrain.relief ?? [], levels: terrain.levels ?? [] };
  const buckets = Array.from({ length: columns * rows }, (): GroundFeatures => ({ landforms: [], relief: [], levels: [] }));
  // Each feature is filed, in its list's order, in every bucket its reach touches; beyond that reach it adds exactly nothing.
  const file = <T>(items: T[], pick: (bucket: GroundFeatures) => T[], reach: (item: T) => [minX: number, maxX: number, minZ: number, maxZ: number]) => {
    for (const item of items) {
      const [minX, maxX, minZ, maxZ] = reach(item);
      for (let row = Math.max(0, Math.floor((minZ + map.depth / 2) / FEATURE_BUCKET)); row <= Math.min(rows - 1, Math.floor((maxZ + map.depth / 2) / FEATURE_BUCKET)); row++)
        for (let column = Math.max(0, Math.floor((minX + map.width / 2) / FEATURE_BUCKET)); column <= Math.min(columns - 1, Math.floor((maxX + map.width / 2) / FEATURE_BUCKET)); column++)
          pick(buckets[row * columns + column]).push(item);
    }
  };
  file(all.landforms, bucket => bucket.landforms, form => [form.x - form.width / 2, form.x + form.width / 2, form.z - form.depth / 2, form.z + form.depth / 2]);
  file(all.relief, bucket => bucket.relief, area => [area.x - area.radius, area.x + area.radius, area.z - area.radius, area.z + area.radius]);
  file(all.levels, bucket => bucket.levels, level => {
    const blend = Math.max(.001, level.blend);
    return [level.x - level.width / 2 - blend, level.x + level.width / 2 + blend, level.z - level.depth / 2 - blend, level.z + level.depth / 2 + blend];
  });
  return { width: map.width, depth: map.depth, landforms: all.landforms.length, relief: all.relief.length, levels: all.levels.length, columns, rows, all, buckets };
}

/**
 * The terrain without plateaus and ramps: landforms, undulation, rolling relief, levels and the shore. The ground settles to
 * sea level towards the tide line except where a raised plateau covers it (`cover`, 0 to 1), so a cliff top keeps its height
 * to the edge.
 */
export function naturalHeight(map: MapDefinition, x: number, z: number, cover = 0) {
  const terrain = map.terrain!, near = groundFeaturesAt(map, x, z);
  let height = 0;
  for (const form of near.landforms) {
    const e = landformWeight(form, x, z);
    if (e === 0) continue;
    const profile = form.surface === 'rock' ? smooth(e * 2) : e * e;
    // Long folds rather than high-frequency bumps: slopes remain easy to walk and animate.
    height += form.height * profile * (1 + Math.sin((x - form.x) * Math.min(.45, 8 / form.width) + (z - form.z) * Math.min(.32, 6 / form.depth)) * .065);
  }
  height += (terrain.undulation ?? 0) * (Math.sin(x * .11 + z * .045) + Math.sin(z * .13 - x * .035)) * .5;
  for (const area of near.relief) {
    const distance = Math.hypot(x - area.x, z - area.z);
    if (distance >= area.radius) continue;
    height += area.height * (1 - smooth((distance - area.radius * .55) / (area.radius * .45))) * rolling(x / area.scale, z / area.scale, area.seed ?? 0);
  }
  const boundary = terrain.continuesBeyond ? 1 : smooth(Math.min(map.width / 2 - Math.abs(x), map.depth / 2 - Math.abs(z)) / 8);
  for (const level of near.levels) {
    const outside = Math.max(0, Math.abs(x - level.x) - level.width / 2, Math.abs(z - level.z) - level.depth / 2);
    const weight = 1 - smooth(outside / Math.max(.001, level.blend));
    height += (level.elevation - height) * weight;
  }
  const shelf = shorelineAt(map, z);
  const shore = shelf ? smooth((x - shelf.x - shelf.width / 2 - 2) / 10) : 1;
  return height * boundary * (shore + (1 - shore) * cover);
}

/** Exact height on the rendered triangles, shared by authoritative snapshots and presentation. */
export function terrainHeight(map: MapDefinition, x: number, z: number): number {
  if (!map.terrain) return 0;
  const { columns, rows, dx, dz } = terrainMesh(map);
  const gx = Math.max(0, Math.min(columns, (x + map.width / 2) / dx)), gz = Math.max(0, Math.min(rows, (z + map.depth / 2) / dz));
  const { i, j, size } = terrainCellAt(map, Math.min(columns - 1, Math.floor(gx)), Math.min(rows - 1, Math.floor(gz)));
  const u = (gx - i) / size, v = (gz - j) / size;
  const a = terrainLatticeHeight(map, i, j), b = terrainLatticeHeight(map, i + size, j), c = terrainLatticeHeight(map, i, j + size);
  return u + v <= 1 ? a * (1 - u - v) + b * u + c * v
    : terrainLatticeHeight(map, i + size, j + size) * (u + v - 1) + b * (1 - v) + c * (1 - u);
}

export function terrainGradient(map: MapDefinition, x: number, z: number) {
  const reach = .15;
  return { x: (terrainHeight(map, x + reach, z) - terrainHeight(map, x - reach, z)) / (reach * 2),
    z: (terrainHeight(map, x, z + reach) - terrainHeight(map, x, z - reach)) / (reach * 2) };
}
